interface HeroConfig {
  theme: 'bengara' | 'gold' | 'cyan';
}

const HERO_MAP: Record<string, HeroConfig> = {
  'craft-hero':      { theme: 'bengara' },
  'prjspage-hero':   { theme: 'bengara' },
  'projpage-hero':   { theme: 'bengara' },
};

const NS = 'http://www.w3.org/2000/svg';

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    el.setAttribute(k, v);
  }
  return el;
}

function buildRingSVG(): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: '0 0 480 480',
    'aria-hidden': 'true',
    class: 'hero-ring-visual__svg',
    xmlns: NS,
  });

  // Outer ring — CW rotation, coarse dashes
  const gOuter = svgEl('g', { class: 'hero-ring-cw' });
  gOuter.appendChild(svgEl('circle', {
    cx: '240', cy: '240', r: '210',
    fill: 'none',
    stroke: 'var(--hr-primary)',
    'stroke-width': '1',
    'stroke-dasharray': '6 14',
  }));
  for (let i = 0; i < 6; i++) {
    const a = (i * 60 * Math.PI) / 180;
    const node = svgEl('circle', {
      cx: (240 + 210 * Math.cos(a)).toFixed(2),
      cy: (240 + 210 * Math.sin(a)).toFixed(2),
      r: '3.5',
      fill: 'var(--hr-primary)',
      class: 'hero-node',
    });
    node.style.animationDelay = `${(i * 0.5).toFixed(1)}s`;
    gOuter.appendChild(node);
  }

  // Middle ring — CCW rotation, fine dots + sweep arc
  const gMid = svgEl('g', { class: 'hero-ring-ccw' });
  gMid.appendChild(svgEl('circle', {
    cx: '240', cy: '240', r: '165',
    fill: 'none',
    stroke: 'var(--hr-secondary)',
    'stroke-width': '1',
    'stroke-dasharray': '2 6',
  }));
  // Arc: full circle path with short dasharray — dashoffset animation sweeps it
  gMid.appendChild(svgEl('circle', {
    cx: '240', cy: '240', r: '165',
    fill: 'none',
    stroke: 'var(--hr-primary)',
    'stroke-width': '2.5',
    'stroke-linecap': 'round',
    'stroke-dasharray': '100 936',
    'stroke-dashoffset': '0',
    class: 'hero-arc',
  }));

  // Inner ring — CW fast, very fine
  const gInner = svgEl('g', { class: 'hero-ring-cw-fast' });
  gInner.appendChild(svgEl('circle', {
    cx: '240', cy: '240', r: '118',
    fill: 'none',
    stroke: 'var(--hr-accent)',
    'stroke-width': '0.6',
    'stroke-dasharray': '1 8',
  }));

  svg.appendChild(gOuter);
  svg.appendChild(gMid);
  svg.appendChild(gInner);

  return svg;
}

export function initHeroVisuals(): void {
  const selector = Object.keys(HERO_MAP).map(cls => `.${cls}`).join(', ');
  const heroEl = document.querySelector<HTMLElement>(selector);
  if (!heroEl) return;

  const heroClass = Object.keys(HERO_MAP).find(cls => heroEl.classList.contains(cls));
  if (!heroClass) return;

  const { theme } = HERO_MAP[heroClass];

  const wrapper = document.createElement('div');
  wrapper.className = `hero-ring-visual hero-ring-visual--${theme}`;
  wrapper.setAttribute('aria-hidden', 'true');
  wrapper.appendChild(buildRingSVG());

  // Ensure we inject into the outer section/header, not an inner wrapper div
  const target = (heroEl.tagName === 'SECTION' || heroEl.tagName === 'HEADER')
    ? heroEl
    : (heroEl.closest<HTMLElement>('section, header') ?? heroEl);
  target.appendChild(wrapper);
}
